import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import {
    SOLUTION_SLUGS,
    getLocalizedSolutionLanding,
} from "../data/solutionLandings";
import { ArrowRight } from "lucide-react";

const SITE_URL = "https://adrise-agency.com";

export default function SolutionsIndexPage() {
    const { language } = useLanguage();
    const items = SOLUTION_SLUGS.map((slug) =>
        getLocalizedSolutionLanding(slug, language),
    ).filter(Boolean);

    const c = {
        en: {
            title: "Adrise Solutions",
            subtitle:
                "Operational solution pages for credit-line infrastructure, platform execution, and crypto-funded scaling.",
            seoTitle: "Adrise Solutions — Agency Ad Account Infrastructure",
            seoDesc:
                "Explore Adrise solution pages for credit-line ad accounts, platform-specific setup, and stablecoin-funded operations.",
        },
        pt: {
            title: "Soluções Adrise",
            subtitle:
                "Páginas operacionais de solução para infraestrutura credit-line, execução por plataforma e escala com cripto.",
            seoTitle: "Soluções Adrise — Infraestrutura de Contas de Agência",
            seoDesc:
                "Explore as soluções da Adrise para contas credit-line, setup por plataforma e operações com stablecoin.",
        },
        es: {
            title: "Soluciones Adrise",
            subtitle:
                "Páginas de solución operativa para infraestructura credit-line, ejecución por plataforma y escalado con cripto.",
            seoTitle: "Soluciones Adrise — Infraestructura de Cuentas de Agencia",
            seoDesc:
                "Explora soluciones Adrise para cuentas credit-line, setup por plataforma y operaciones con stablecoins.",
        },
        ru: {
            title: "Решения Adrise",
            subtitle:
                "Операционные solution-страницы по credit-line инфраструктуре, platform execution и crypto-funded масштабированию.",
            seoTitle: "Решения Adrise — Инфраструктура агентских аккаунтов",
            seoDesc:
                "Изучите решения Adrise: credit-line аккаунты, platform setup и операции на стейблкоинах.",
        },
        tr: {
            title: "Adrise Çözümleri",
            subtitle:
                "Credit-line altyapısı, platform bazlı yürütme ve kripto fonlu ölçekleme için operasyonel çözüm sayfaları.",
            seoTitle: "Adrise Çözümleri — Ajans Hesap Altyapısı",
            seoDesc:
                "Credit-line reklam hesapları, platform setup ve stablecoin tabanlı operasyonlar için Adrise çözüm sayfalarını keşfedin.",
        },
    }[language] || {
        title: "Adrise Solutions",
        subtitle:
            "Operational solution pages for credit-line infrastructure, platform execution, and crypto-funded scaling.",
        seoTitle: "Adrise Solutions — Agency Ad Account Infrastructure",
        seoDesc:
            "Explore Adrise solution pages for credit-line ad accounts, platform-specific setup, and stablecoin-funded operations.",
    };

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: c.title,
        url: `${SITE_URL}/solutions`,
        hasPart: items.map((i) => ({
            "@type": "WebPage",
            name: i.title,
            url: `${SITE_URL}/solutions/${i.slug}`,
        })),
    };

    return (
        <main data-testid="solutions-page" className="bg-background text-foreground min-h-screen">
            <SEOHead
                title={c.seoTitle}
                description={c.seoDesc}
                canonical={`${SITE_URL}/solutions`}
                keywords={[
                    "adrise solutions",
                    "agency ad account solutions",
                    "credit line ad accounts",
                ]}
                jsonLd={jsonLd}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-16" data-testid="solutions-index-hero">
                <div className="container-skala">
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        {c.title}
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-3xl leading-relaxed">
                        {c.subtitle}
                    </p>
                </div>
            </section>

            <section className="pb-20" data-testid="solutions-index-grid-section">
                <div className="container-skala grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item) => (
                        <Link
                            key={item.slug}
                            to={`/solutions/${item.slug}`}
                            data-testid={`solutions-index-card-${item.slug}`}
                            className="rounded-2xl border border-white/[0.08] bg-card p-5 hover:border-primary/40 transition-colors"
                        >
                            <div className="aspect-[16/10] rounded-xl overflow-hidden mb-4 border border-white/10">
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <h2 className="text-base md:text-lg font-semibold leading-snug">
                                {item.title}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                                {item.subtitle}
                            </p>
                            <p className="text-xs text-primary mt-4 inline-flex items-center gap-1.5">
                                {({ tr: "Çözümü aç", es: "Abrir solución", pt: "Abrir solução", ru: "Открыть решение" })[language] || "Open solution"} <ArrowRight className="w-3.5 h-3.5" />
                            </p>
                        </Link>
                    ))}
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
